import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faEye,
  faEyeSlash,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import AuthenticationService from "../../_services/AuthenticationService";
import Config from "../../_config/config";
import { ITeachConfigContext } from "../../_context/PresetContext";
import type { ITeachAppConfig } from "../../_context/PresetContext";
import "../login/login.css";
import "./forgot-password.css";

const OTP_EXPIRY_SECONDS = 600; // 10 minutes

const ForgotPassword: React.FC = () => {
  const history = useHistory();
  const { config } = useContext(ITeachConfigContext) as {
    config: ITeachAppConfig;
    loading: boolean;
  };

  const [step, setStep] = useState(1);
  const [username, setUsername] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"error" | "success">("error");
  const [otpInfo, setOtpInfo] = useState<any>(null);
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (countdown > 0) {
      timerRef.current = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [countdown]);

  useEffect(() => {
    if (step === 3) {
      const timer = setTimeout(() => history.push("/login"), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [step, history]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handleSendOtp = () => {
    if (!username.trim()) {
      setMessage("Please enter your username.");
      setMessageType("error");
      return;
    }
    setLoading(true);
    setMessage("");
    AuthenticationService.recoverPassword(username.trim())
      .then((res) => {
        setLoading(false);
        if (res.data?.status === 1) {
          setOtpInfo(res.data.data);
          setStep(2);
          setCountdown(OTP_EXPIRY_SECONDS);
          setMessage("");
          setOtp("");
          setNewPassword("");
          setConfirmPassword("");
        } else {
          setMessage(res.data?.message || "Failed to send OTP.");
          setMessageType("error");
        }
      })
      .catch(() => {
        setLoading(false);
        setMessage("Something went wrong. Please try again.");
        setMessageType("error");
      });
  };

  const handleResendOtp = () => {
    setMessage("");
    handleSendOtp();
  };

  const handleResetPassword = () => {
    if (!otp.trim()) {
      setMessage("Please enter the OTP.");
      setMessageType("error");
      return;
    }
    if (otpInfo && otp.trim().length !== otpInfo.otpLength) {
      setMessage(`OTP must be ${otpInfo.otpLength} digits.`);
      setMessageType("error");
      return;
    }
    if (!newPassword) {
      setMessage("Please enter a new password.");
      setMessageType("error");
      return;
    }
    if (newPassword.length < 6) {
      setMessage("Password must be at least 6 characters.");
      setMessageType("error");
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage("Passwords do not match.");
      setMessageType("error");
      return;
    }
    if (countdown === 0) {
      setMessage("OTP has expired. Please resend.");
      setMessageType("error");
      return;
    }

    setLoading(true);
    setMessage("");
    AuthenticationService.resetPassword(
      username.trim(),
      otp.trim(),
      newPassword,
      confirmPassword
    )
      .then((res) => {
        setLoading(false);
        if (res.data?.status === 1) {
          setStep(3);
          setMessage("");
        } else {
          setMessage(res.data?.message || "Failed to reset password.");
          setMessageType("error");
        }
      })
      .catch(() => {
        setLoading(false);
        setMessage("Something went wrong. Please try again.");
        setMessageType("error");
      });
  };

  const renderStepIndicator = () => {
    const steps = [1, 2, 3];
    return (
      <div className="fp-steps">
        {steps.map((s, i) => (
          <React.Fragment key={s}>
            <div
              className={`fp-step ${
                step > s ? "completed" : step === s ? "active" : "pending"
              }`}
            >
              {step > s ? "✓" : s}
            </div>
            {i < steps.length - 1 && (
              <div className={`fp-step-line ${step > s ? "completed" : ""}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderStep1 = () => (
    <form
      className="modern-login-form"
      onSubmit={(e) => {
        e.preventDefault();
        handleSendOtp();
      }}
    >
      <div className="form-field">
        <label htmlFor="fp-username">Username</label>
        <input
          type="text"
          id="fp-username"
          className="modern-input"
          placeholder="Enter your username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoFocus
        />
      </div>
      {message && <div className={`fp-message ${messageType}`}>{message}</div>}
      <button type="submit" className="modern-login-btn" disabled={loading}>
        {loading ? (
          <>
            <span className="btn-spinner" />
            Sending...
          </>
        ) : (
          "Send OTP"
        )}
      </button>
    </form>
  );

  const renderStep2 = () => (
    <form
      className="modern-login-form"
      onSubmit={(e) => {
        e.preventDefault();
        handleResetPassword();
      }}
    >
      {otpInfo && otpInfo.otpMessage && (
        <div className="fp-otp-message">{otpInfo.otpMessage}</div>
      )}
      {countdown > 0 ? (
        <div className="fp-timer">OTP expires in {formatTime(countdown)}</div>
      ) : (
        <div className="fp-timer expired">OTP has expired</div>
      )}
      <div className="form-field">
        <label htmlFor="fp-otp">OTP</label>
        <input
          type="text"
          id="fp-otp"
          className="modern-input"
          placeholder="Enter OTP"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
          maxLength={otpInfo ? otpInfo.otpLength : 6}
          autoComplete="one-time-code"
          autoFocus
        />
      </div>
      <div className="form-field">
        <label htmlFor="fp-new-password">New Password</label>
        <div style={{ position: "relative" }}>
          <input
            type={showPassword ? "text" : "password"}
            id="fp-new-password"
            className="modern-input"
            placeholder="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            style={{ paddingRight: "45px" }}
          />
          <button
            type="button"
            className="fp-eye-toggle"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
          </button>
        </div>
      </div>
      <div className="form-field">
        <label htmlFor="fp-confirm-password">Confirm Password</label>
        <div style={{ position: "relative" }}>
          <input
            type={showConfirmPassword ? "text" : "password"}
            id="fp-confirm-password"
            className="modern-input"
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            style={{ paddingRight: "45px" }}
          />
          <button
            type="button"
            className="fp-eye-toggle"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
          >
            <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
          </button>
        </div>
        <span className="fp-hint">Minimum 6 characters</span>
      </div>
      {message && <div className={`fp-message ${messageType}`}>{message}</div>}
      <button
        type="submit"
        className="modern-login-btn"
        disabled={loading || countdown === 0}
      >
        {loading ? (
          <>
            <span className="btn-spinner" />
            Resetting...
          </>
        ) : (
          "Reset Password"
        )}
      </button>
      <div className="fp-resend">
        <span onClick={handleResendOtp}>Resend OTP</span>
      </div>
    </form>
  );

  const renderStep3 = () => (
    <div className="fp-success">
      <div className="fp-success-icon">
        <FontAwesomeIcon icon={faCheckCircle} />
      </div>
      <p className="fp-success-text">
        Your password has been reset. You can now sign in with your new password.
      </p>
      <button
        type="button"
        className="modern-login-btn"
        onClick={() => history.push("/login")}
      >
        Go to Login
      </button>
      <p className="fp-hint" style={{ textAlign: "center", marginTop: 12 }}>
        Redirecting in a few seconds...
      </p>
    </div>
  );

  const defaultLogoUrl = `https://webfront.edusquares.com/${Config.site}/files/logos/logo.png`;
  const tenantLogoUrl = config?.logoUrl || defaultLogoUrl;

  const headings: Record<number, { title: string; subtitle: string }> = {
    1: {
      title: "Recover Password",
      subtitle: "Enter your username to receive a verification code",
    },
    2: {
      title: "Reset Password",
      subtitle: "Enter the OTP and choose a new password",
    },
    3: {
      title: "Password Reset Successful",
      subtitle: "You're all set",
    },
  };

  return (
    <div className="modern-login-container">
      {/* Left Side - Branding (mirrors login) */}
      <div className="login-branding">
        <div className="branding-content">
          <div className="logo-section">
            <div className="logo-icon">
              <img
                src={tenantLogoUrl}
                alt="Institute Logo"
                style={{ width: "80px", height: "80px", objectFit: "contain" }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = "none";
                }}
              />
            </div>
            <h1 className="brand-name">
              {config?.displayName || Config.tenantDisplayName}
            </h1>
          </div>
          <p className="brand-tagline">Empowering Education Through Technology</p>
          <div className="feature-list">
            <div className="feature-item">
              <span className="feature-icon">✓</span>
              <span>Comprehensive Learning Management</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">✓</span>
              <span>Real-time Performance Analytics</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">✓</span>
              <span>Seamless Communication</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="login-form-section">
        <div className="login-form-container">
          {renderStepIndicator()}
          <div className="login-header">
            <h2>{headings[step].title}</h2>
            <p>{headings[step].subtitle}</p>
          </div>

          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}

          {step !== 3 && (
            <div className="fp-link">
              {step === 2 ? (
                <span
                  onClick={() => {
                    setStep(1);
                    setMessage("");
                  }}
                >
                  <FontAwesomeIcon icon={faArrowLeft} size="sm" /> Back
                </span>
              ) : (
                <span onClick={() => history.push("/login")}>
                  <FontAwesomeIcon icon={faArrowLeft} size="sm" /> Back to Login
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
